import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { openTrackedLink, resolveImg } from "../../lib/api";
import GroupHeader from "./GroupHeader";

/**
 * One visual template for every content item — cards, products, services, links.
 *
 * Renders the product-card style the user picked (image left, brand bold,
 * highlight badge + price/meta, CTA). Items with a `category` field are grouped
 * under a shared centered white heading (matches CreditCards + Products style).
 */
function ItemCard({ item, sectionId }) {
  const cta = item.cta_label || "Learn more";
  return (
    <motion.button
      whileTap={{ scale: 0.99 }}
      onClick={() =>
        openTrackedLink(item.url, {
          item_type: sectionId || "item",
          item_id: item.id,
          item_name: item.name,
        })
      }
      data-testid={`section-item-${item.id}`}
      className="group flex w-full items-start gap-3 rounded-2xl p-3 text-left"
      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
    >
      {item.image_url ? (
        <img src={resolveImg(item.image_url)} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" />
      ) : (
        <div className="h-20 w-20 shrink-0 rounded-xl" style={{ background: "rgba(255,255,255,0.06)" }} />
      )}
      <div className="min-w-0 flex-1">
        {item.brand ? <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/55">{item.brand}</p> : null}
        <p className="mt-0.5 text-[16px] font-bold leading-tight text-white">{item.name}</p>
        {item.description ? <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-white/60">{item.description}</p> : null}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {item.highlight ? (
            <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]" style={{ background: "rgba(184,74,38,0.18)", color: "#F9B98F" }}>
              {item.highlight}
            </span>
          ) : null}
          {item.price_or_meta ? <span className="text-[13px] font-bold text-white">{item.price_or_meta}</span> : null}
        </div>
        {(item.meta_extras || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.meta_extras.map((m, i) => (
              <span key={i} className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-white/65" style={{ background: "rgba(255,255,255,0.06)" }}>
                {m}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-1 text-[12px] font-bold" style={{ color: "#F9B98F" }}>
          {cta}
          <ExternalLink size={12} className="opacity-70 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </motion.button>
  );
}

/**
 * Section content — groups items by their `category` (preserving order), with
 * a shared centered white heading for each group. Ungrouped items appear first.
 */
export default function SectionContent({ items = [], sectionId }) {
  if (items.length === 0) {
    return (
      <p className="text-center text-sm text-white/50">
        Add items to this section from the Admin → Content tab.
      </p>
    );
  }
  // Preserve first-seen order of category names.
  const groups = [];
  const seen = new Map();
  for (const it of items) {
    const key = (it.category || "").trim();
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ key, items: [] });
    }
    groups[seen.get(key)].items.push(it);
  }
  return (
    <div className="space-y-8">
      {groups.map((g) => (
        <div key={g.key || "__ungrouped__"}>
          {g.key ? <GroupHeader title={g.key} /> : null}
          <div className="space-y-3">
            {g.items.map((it) => (
              <ItemCard key={it.id} item={it} sectionId={sectionId} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
